import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { api } from '../lib/api';

export default function Users() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');

  const { data: usersData, isLoading } = useQuery({
    queryKey: ['users', searchTerm, roleFilter],
    queryFn: () => api.users.list(searchTerm, roleFilter),
  });

  const updateRoleMutation = useMutation({
    mutationFn: ({ id, global_role }) => api.users.updateRole(id, global_role),
    onSuccess: () => {
      queryClient.invalidateQueries(['users']);
    },
  });

  const users = usersData?.users || [];

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'ADMIN':
        return 'bg-sonarges-red-100 text-sonarges-red-700 dark:bg-sonarges-red-900/30 dark:text-sonarges-red-400 border-sonarges-red-300 dark:border-sonarges-red-700';
      case 'ORGANIZER':
        return 'bg-sonarges-green-100 text-sonarges-green-700 dark:bg-sonarges-green-900/30 dark:text-sonarges-green-400 border-sonarges-green-300 dark:border-sonarges-green-700';
      default:
        return 'bg-sonarges-gray-100 text-sonarges-gray-700 dark:bg-sonarges-gray-700 dark:text-sonarges-gray-300 border-sonarges-gray-300 dark:border-sonarges-gray-600';
    }
  };

  const handleRoleChange = (userId, newRole) => {
    if (window.confirm(t('users.confirmRoleChange') || 'Êtes-vous sûr de vouloir changer ce rôle?')) {
      updateRoleMutation.mutate({ id: userId, global_role: newRole });
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-sonarges-gray-900 dark:text-white">
              {t('users.title')}
            </h1>
            <p className="mt-2 text-sonarges-gray-600 dark:text-sonarges-gray-400">
              Gérez les utilisateurs et leurs privilèges d'accès
            </p>
          </div>
        </div>

        <div className="bg-white dark:bg-sonarges-gray-800 rounded-xl shadow-lg p-6 border border-sonarges-gray-200 dark:border-sonarges-gray-700">
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex-1">
              <div className="relative">
                <input
                  type="text"
                  placeholder={t('users.search')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-3 pl-12 border-2 border-sonarges-gray-200 dark:border-sonarges-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-sonarges-green-500 focus:border-transparent bg-white dark:bg-sonarges-gray-700 text-sonarges-gray-900 dark:text-white transition-colors duration-200"
                />
                <svg className="absolute left-4 top-3.5 w-5 h-5 text-sonarges-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>

            <div className="w-full md:w-64">
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="w-full px-4 py-3 border-2 border-sonarges-gray-200 dark:border-sonarges-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-sonarges-green-500 focus:border-transparent bg-white dark:bg-sonarges-gray-700 text-sonarges-gray-900 dark:text-white transition-colors duration-200"
              >
                <option value="">{t('users.allRoles')}</option>
                <option value="ADMIN">{t('users.ADMIN')}</option>
                <option value="ORGANIZER">{t('users.ORGANIZER')}</option>
                <option value="MEMBER">{t('users.MEMBER')}</option>
              </select>
            </div>
          </div>

          {isLoading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-sonarges-green-600"></div>
              <p className="mt-4 text-sonarges-gray-600 dark:text-sonarges-gray-400">{t('auth.loading')}</p>
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-12">
              <svg className="w-16 h-16 mx-auto text-sonarges-gray-400 dark:text-sonarges-gray-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <p className="text-sonarges-gray-600 dark:text-sonarges-gray-400">Aucun utilisateur trouvé</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-sonarges-gray-200 dark:border-sonarges-gray-700">
                    <th className="text-left py-4 px-4 font-semibold text-sonarges-gray-700 dark:text-sonarges-gray-300">
                      {t('users.name')}
                    </th>
                    <th className="text-left py-4 px-4 font-semibold text-sonarges-gray-700 dark:text-sonarges-gray-300">
                      {t('users.email')}
                    </th>
                    <th className="text-left py-4 px-4 font-semibold text-sonarges-gray-700 dark:text-sonarges-gray-300">
                      {t('users.globalRole')}
                    </th>
                    <th className="text-right py-4 px-4 font-semibold text-sonarges-gray-700 dark:text-sonarges-gray-300">
                      {t('common.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-sonarges-gray-200 dark:divide-sonarges-gray-700">
                  {users.map((user) => (
                    <tr key={user.id} className="hover:bg-sonarges-gray-50 dark:hover:bg-sonarges-gray-700/50 transition-colors">
                      <td className="py-4 px-4">
                        <div className="flex items-center">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-sonarges-green-500 to-sonarges-green-600 flex items-center justify-center text-white font-bold text-lg">
                            {user.name.charAt(0).toUpperCase()}
                          </div>
                          <span className="ml-3 font-medium text-sonarges-gray-900 dark:text-white">
                            {user.name}
                          </span>
                        </div>
                      </td>
                      <td className="py-4 px-4 text-sonarges-gray-600 dark:text-sonarges-gray-400">
                        {user.email}
                      </td>
                      <td className="py-4 px-4">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold border ${getRoleBadgeColor(user.global_role)}`}>
                          {t(`users.${user.global_role}`)}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-right">
                        <select
                          value={user.global_role}
                          onChange={(e) => handleRoleChange(user.id, e.target.value)}
                          disabled={updateRoleMutation.isPending}
                          className="px-3 py-1.5 border-2 border-sonarges-gray-300 dark:border-sonarges-gray-600 rounded-lg bg-white dark:bg-sonarges-gray-700 text-sonarges-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-sonarges-green-500 disabled:opacity-50 transition-colors"
                        >
                          <option value="ADMIN">{t('users.ADMIN')}</option>
                          <option value="ORGANIZER">{t('users.ORGANIZER')}</option>
                          <option value="MEMBER">{t('users.MEMBER')}</option>
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="bg-gradient-to-r from-sonarges-green-50 to-sonarges-gray-50 dark:from-sonarges-green-900/20 dark:to-sonarges-gray-800 rounded-xl p-6 border border-sonarges-green-200 dark:border-sonarges-green-800">
          <div className="flex items-start">
            <svg className="w-6 h-6 text-sonarges-green-600 dark:text-sonarges-green-400 mr-3 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h3 className="font-semibold text-sonarges-gray-900 dark:text-white mb-2">Rôles et Privilèges</h3>
              <ul className="space-y-2 text-sm text-sonarges-gray-700 dark:text-sonarges-gray-300">
                <li><span className="font-semibold text-sonarges-red-600 dark:text-sonarges-red-400">ADMIN:</span> Accès complet à toutes les fonctionnalités et instances</li>
                <li><span className="font-semibold text-sonarges-green-600 dark:text-sonarges-green-400">ORGANIZER:</span> Peut créer et gérer des instances</li>
                <li><span className="font-semibold text-sonarges-gray-600 dark:text-sonarges-gray-400">MEMBER:</span> Accès en lecture, doit être invité aux instances</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
